import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { recipesData } from '../data/recipes';
import './Recipes.css';

// Import local assets
import heroImg from '../assets/spices2.jpg';
import herbImg from '../assets/kasuri methi.jpg';
import turmericImg from '../assets/turmeric.jpg';
import chilliImg from '../assets/chilli powder.jpg';
import dhaneImg from '../assets/dhane powder.jpg';

const recipeCategories = ['All', 'Main Course', 'Appetizers', 'Drinks', 'Desserts'];

const Recipes = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRecipes = recipesData.filter(recipe => {
    const matchesCategory = activeCategory === 'All' || recipe.category === activeCategory;
    const matchesSearch = recipe.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="recipes-page">
      {/* BOUTIQUE CREATIVE HERO SECTION */}
      <section className="recipes-hero-boutique">
        <div className="boutique-bg-text">HERITAGE</div>
        
        <div className="container">
          <div className="boutique-hero-grid">
            <motion.div 
              className="boutique-hero-text"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="boutique-label">
                <span className="line" />
                ARTISANAL ARCHIVES
              </div>
              <h1 className="boutique-title">
                The Soul of <br />
                <span className="serif-italic">Indian</span> Kitchen
              </h1>
              <p className="boutique-p">
                Unlock generations of culinary wisdom. Our recipes are curated to celebrate 
                the depth of stone-ground purity.
              </p>
              
              <div className="boutique-cta-group">
                <a href="#explore" className="btn-primary">Explore Collection</a>
                <div className="recipe-count">
                  <strong>24+</strong>
                  <span>Secret Blends</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="boutique-hero-visual"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <div className="creative-img-frame">
                <img src={heroImg} alt="Spices" className="main-boutique-img" />
                <div className="img-overlay-accent" />
                
                {/* Floating Elements */}
                <motion.div 
                  className="floating-spice-tag"
                  animate={{ y: [0, -20, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <span className="tag-icon">🌿</span>
                  <div>
                    <p>100% Purity</p>
                    <small>Stone-Ground</small>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="scroll-indicator-wrap">
          <div className="scroll-line" />
          <span>SCROLL</span>
        </div>
      </section>

      {/* NEW SECTION: EXHIBITION OF ESSENCE (Inspired by Screenshot) */}
      <section className="recipe-exhibition-section">
        <div className="container">
          <motion.div 
            className="exhibition-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="accent-sub">Artisanal Exhibition</span>
            <h2 className="exhibition-title">The Essence of <span className="serif-italic">Pure Blends</span></h2>
          </motion.div>

          <div className="exhibition-grid">
            {[
              { id: '01', name: 'Verdant Herbs', desc: 'Sustainably sourced, fresh-dried vitality.', img: herbImg },
              { id: '02', name: 'Golden Earth', desc: 'Meghalaya’s sun-kissed healing gold.', img: turmericImg },
              { id: '03', name: 'Crimson Fire', desc: 'Kashmiri warmth in every hand-picked pod.', img: chilliImg },
              { id: '04', name: 'Ochre Sand', desc: 'Ancient stone-ground heritage blends.', img: dhaneImg }
            ].map((item, i) => (
              <motion.div 
                key={item.id}
                className="exhibition-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
              >
                <div className="exhibition-visual">
                  <div className="bowl-frame">
                    <img src={item.img} alt={item.name} />
                  </div>
                  <div className="exhibition-number">{item.id}</div>
                </div>
                <div className="exhibition-info">
                  <h4>{item.name}</h4>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter & Search Section */}
      <section className="recipes-controls">
        <div className="container">
          <div className="controls-inner">
            <div className="categories-list">
              {recipeCategories.map(cat => (
                <button 
                  key={cat}
                  className={`cat-btn ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                  data-cursor
                >
                  {cat}
                  {activeCategory === cat && (
                    <motion.div 
                      layoutId="activeCat" 
                      className="cat-dot" 
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </button>
              ))}
            </div>
            
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input 
                type="text" 
                placeholder="Find a recipe..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recipes Grid */}
      <section className="recipes-grid-section">
        <div className="container">
          <motion.div className="recipes-grid" layout>
            <AnimatePresence mode="popLayout">
              {filteredRecipes.map((recipe, index) => (
                <motion.div 
                  key={recipe.id}
                  className="recipe-card"
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  data-cursor
                >
                  <Link to={`/recipe/${recipe.id}`} className="recipe-card-link-wrapper">
                    <div className="recipe-img-wrap">
                      <img src={recipe.img} alt={recipe.name} />
                      <div className="recipe-overlay-meta">
                        <span>{recipe.time}</span>
                        <span>{recipe.difficulty}</span>
                      </div>
                    </div>
                  </Link>
                  <div className="recipe-info">
                    <span className="recipe-cat-tag">{recipe.category}</span>
                    <h3>{recipe.name}</h3>
                    <p className="recipe-masala-tag">Crafted with {recipe.masala}</p>
                    <Link to={`/recipe/${recipe.id}`} className="view-recipe-btn">
                      Explore Now
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14m-7-7 7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
          
          {filteredRecipes.length === 0 && (
            <motion.div 
              className="no-results text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="story-long-text">No recipes found matching your search. Try another flavour!</p>
            </motion.div>
          )}
        </div>
      </section>

    </div>
  );
};

export default Recipes;
